/**
 * Design Demo Client Component
 * Full-page preview for wedding invitation designs
 * Opens in new tab - user closes tab to exit
 */

'use client'

import { useEffect, useState } from 'react'
import type {
  TemplateComponent,
  TemplateData,
  FullPagePreviewProps,
} from '@kiltechco/dday-templates'
import { getPackageConfig } from '@/lib/ddayTemplates'
import { logger } from '@/lib/logger'
import { ensureRuntimeConfigWithMapsKey } from '@/src/lib/runtime-config-bootstrap.client'

interface DesignDemoClientProps {
  heroId: string
}

type FullPagePreviewComponent = React.ComponentType<FullPagePreviewProps>
export default function DesignDemoClient({ heroId }: DesignDemoClientProps) {
  const [Template, setTemplate] = useState<TemplateComponent | null>(null)
  const [FullPagePreview, setFullPagePreview] =
    useState<FullPagePreviewComponent | null>(null)
  const [templateData, setTemplateData] = useState<TemplateData | null>(null)

  useEffect(() => {
    let cancelled = false

    const loadResources = async () => {
      const templatesModule = await import('@kiltechco/dday-templates')
      if (cancelled) return

      // Demo routes can hydrate before runtime config is available in-memory.
      await ensureRuntimeConfigWithMapsKey()
      if (cancelled) return

      templatesModule.setPackageConfig(getPackageConfig())

      const demo = templatesModule.getHeroDemo(heroId)
      if (!demo || cancelled) return

      setTemplateData(demo.data)
      setFullPagePreview(() => templatesModule.FullPagePreview)

      const templateComponent = await templatesModule.getTemplate()
      if (!cancelled) {
        setTemplate(() => templateComponent)
      }
    }

    loadResources().catch(logger.error)

    return () => {
      cancelled = true
    }
  }, [heroId])

  if (!Template || !FullPagePreview || !templateData) {
    return (
      <div className="fixed inset-0 bg-background flex items-center justify-center z-50">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <FullPagePreview>
      <Template
        templateData={templateData}
        mode="preview"
        renderContext="standalone"
      />
    </FullPagePreview>
  )
}
